import { handleSendCiedComingSoonRequest } from '@/entities/support';
import {
  auth,
  checkToFormatPhoneNumber,
  formatPhoneNumber,
  generateFullName,
  showMessageError,
  showMessageSuccess,
} from '@/shared/lib';
import { useMergeState } from '@/shared/lib/customHooks';
import _ from 'lodash';
import { t } from 'ttag';
import { facilitySelection } from '@/entities/facility';
import type { LearnMoreUniversalCiedManagementModalProps } from './learnMoreUniversalCiedManagementModal.types';
import { ERROR_IP_SPAM } from '../../lib/learnMoreModal';

/**
 * Backs the universal CIED management Learn More modal that records the clinic user's interest.
 * Keeps an isLoading flag in merged state and exposes the click handler that sends the interest
 * request and closes the modal through handleClickCloseButton.
 *
 * @param props - The handleClickCloseButton callback that closes the modal.
 * @returns The loading state and the Learn More click handler.
 */
export const useLearnMoreUniversalCiedManagementModal = ({
  handleClickCloseButton,
}: Pick<LearnMoreUniversalCiedManagementModalProps, 'handleClickCloseButton'>) => {
  const [state, setState] = useMergeState({
    isLoading: false,
  });

  /**
   * Registers the clinic user's interest in universal CIED management from the marketing banner modal. Builds the request from auth data (user id, full name, email, phone formatted when the country requires it, and support facility names), sends it with handleSendCiedComingSoonRequest and closes the modal on success; on failure it logs, and for ERROR_IP_SPAM shows that message and closes, otherwise shows the error.
   *
   * @returns A promise resolving once the interest request has been sent and the modal closed.
   */
  const onClickLearnMore = async () => {
    try {
      setState({ isLoading: true });
      const shouldFormatPhoneNumber = checkToFormatPhoneNumber(auth.country());
      const phone = auth.phone1();
      const userInfo = {
        firstName: auth.firstName(),
        lastName: auth.lastName(),
      };
      const input = {
        userId: auth.userId(),
        fullname: generateFullName(userInfo),
        email: auth.email(),
        phone: shouldFormatPhoneNumber ? formatPhoneNumber(phone) : phone,
        facilities: _.map(facilitySelection.userSupportFacilities(), (x) => x.name),
      };
      await handleSendCiedComingSoonRequest(input);
      showMessageSuccess({
        content: t`Your interest has been forwarded to our team, and we will contact you shortly.`,
      });
      handleClickCloseButton?.();
    } catch (error) {
      console.error('Failed to send CIED coming soon request: ', error);
      const message = error instanceof Error ? error.message : undefined;
      if (_.trim(message) === ERROR_IP_SPAM) {
        showMessageError({ content: ERROR_IP_SPAM });
        handleClickCloseButton?.();
        return;
      }
      showMessageError({ content: message });
    } finally {
      setState({ isLoading: false });
    }
  };

  return {
    state,
    onClickLearnMore,
  };
};
